import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { duesTrayStyles } from './lib/dues-tray-styles.js';
import { PortalHost } from './lib/portal-host.js';
import { SevaFreezable } from './lib/freezable-mixin.js';
import { SEVA_EVENTS, type OpenDuesDetail } from './lib/events.js';
import { DEFAULT_DUES_DRAWER_TITLE, DUES_HEADER_CHANGE } from './lib/dues-drawer.js';
import { DEFAULT_API_URL } from './lib/constants.js';
import type { SevaDuesFlow } from './seva-dues-flow.js';

const CLOSE_ICON = '\u2715'; // ✕

@customElement('seva-dues-tray')
export class SevaDuesTray extends SevaFreezable(PortalHost) {
	@property({ attribute: 'tenant-slug' }) tenantSlug = '';
	@property({ attribute: 'api-url' }) apiUrl = DEFAULT_API_URL;
	@property({ attribute: 'return-url' }) returnUrl = '';
	@property({ reflect: true }) theme: 'light' | 'dark' = 'light';

	@state() private checkoutActive = false;
	@state() private startView: OpenDuesDetail['view'] = undefined;
	@state() private updateDuesMembershipId = '';
	@state() private drawerTitle = DEFAULT_DUES_DRAWER_TITLE;

	private closeTimer: ReturnType<typeof setTimeout> | null = null;

	protected get portalStyles(): string {
		return duesTrayStyles;
	}

	protected get portalMarker(): string {
		return 'data-seva-dues-tray';
	}

	protected get panelSelector(): string {
		return '.drawer';
	}

	protected override onShow(): void {
		const closeBtn = this.portalShadow?.querySelector('.close-btn') as HTMLElement | null;
		closeBtn?.focus();
	}

	protected override onHide(): void {
		const flow = this.portalShadow?.querySelector('seva-dues-flow') as SevaDuesFlow | null;
		flow?.destroyCheckout();
		this.checkoutActive = false;
		this.startView = undefined;
		this.updateDuesMembershipId = '';
		this.drawerTitle = DEFAULT_DUES_DRAWER_TITLE;
	}

	override connectedCallback(): void {
		super.connectedCallback();
		document.addEventListener(SEVA_EVENTS.OPEN_DUES, this.handleOpenDues as EventListener);
		document.addEventListener(
			SEVA_EVENTS.DUES_CHECKOUT_COMPLETE,
			this.handleCheckoutComplete as EventListener
		);
		document.addEventListener(
			'seva:dues-checkout-started',
			this.handleCheckoutStarted as EventListener
		);
		document.addEventListener(
			'seva:dues-checkout-canceled',
			this.handleCheckoutCanceled as EventListener
		);
		document.addEventListener(
			SEVA_EVENTS.DUES_COMPLETE_DISMISS,
			this.handleCompleteDismiss as EventListener
		);
		document.addEventListener(
			SEVA_EVENTS.DUES_PENDING_STATUS,
			this.handlePendingStatusHandoff as EventListener
		);
		document.addEventListener(DUES_HEADER_CHANGE, this.handleHeaderChange as EventListener);
	}

	override disconnectedCallback(): void {
		super.disconnectedCallback();
		document.removeEventListener(SEVA_EVENTS.OPEN_DUES, this.handleOpenDues as EventListener);
		document.removeEventListener(
			SEVA_EVENTS.DUES_CHECKOUT_COMPLETE,
			this.handleCheckoutComplete as EventListener
		);
		document.removeEventListener(
			'seva:dues-checkout-started',
			this.handleCheckoutStarted as EventListener
		);
		document.removeEventListener(
			'seva:dues-checkout-canceled',
			this.handleCheckoutCanceled as EventListener
		);
		document.removeEventListener(
			SEVA_EVENTS.DUES_COMPLETE_DISMISS,
			this.handleCompleteDismiss as EventListener
		);
		document.removeEventListener(
			SEVA_EVENTS.DUES_PENDING_STATUS,
			this.handlePendingStatusHandoff as EventListener
		);
		document.removeEventListener(DUES_HEADER_CHANGE, this.handleHeaderChange as EventListener);
		if (this.closeTimer) clearTimeout(this.closeTimer);
	}

	private handleOpenDues = (e: CustomEvent<OpenDuesDetail | undefined>) => {
		const detail = e.detail;
		this.startView = detail?.view;
		if (detail?.view === 'update-payment-method') {
			this.updateDuesMembershipId = detail.duesMembershipId;
		} else {
			this.updateDuesMembershipId = '';
		}
		this.drawerTitle = DEFAULT_DUES_DRAWER_TITLE;
		this.show();
	};

	private handleCheckoutComplete = () => {
		this.checkoutActive = false;
		this.closeTimer = setTimeout(() => {
			this.hide();
		}, 4000);
	};

	private handleCheckoutStarted = () => {
		this.checkoutActive = true;
	};

	private handleCheckoutCanceled = () => {
		this.checkoutActive = false;
	};

	private handleCompleteDismiss = () => {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}
		this.hide();
	};

	private handlePendingStatusHandoff = () => {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}
		this.hide();
	};

	private handleHeaderChange = (e: CustomEvent<{ title: string }>) => {
		this.drawerTitle = e.detail.title;
	};

	private handleBackdropClick = () => this.hide();

	protected renderPortalContent(): TemplateResult {
		return html`
			<div class=${`backdrop${this.open ? ' open' : ''}`} @click=${this.handleBackdropClick}></div>

			<div
				class=${`drawer${this.open ? ' open' : ''}${this.checkoutActive ? ' checkout-active' : ''}`}
				role="dialog"
				aria-label=${this.drawerTitle}
				aria-modal="true"
			>
				<div class="drawer-header">
					<h2 class="drawer-title">${this.drawerTitle}</h2>
					<button class="close-btn" @click=${() => this.hide()} aria-label="Close dues payment">
						${CLOSE_ICON}
					</button>
				</div>
				<div class="drawer-body">
					${this.open
						? html`<seva-dues-flow
								tenant-slug=${this.tenantSlug}
								api-url=${this.apiUrl}
								return-url=${this.returnUrl}
								theme=${this.theme}
								presentation="tray"
								start-view=${this.startView ?? nothing}
								update-dues-membership-id=${this.updateDuesMembershipId || nothing}
							></seva-dues-flow>`
						: nothing}
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'seva-dues-tray': SevaDuesTray;
	}
}
